/**
 * PlannerTimeline — the multi-track Gantt canvas (§13/§13a). A left rail of
 * grouped, CPE-aware track labels aligned to a Plotly horizontal-bar Gantt
 * (plan outline + actual fill, NPT, offline, Today line, multi-scale axis).
 *
 * Op colours are resolved from the live --op-* tokens off the document root so
 * the chart follows the theme/palette (never hardcoded; §20 keeps --brand out
 * of data viz). The figure math lives in `buildGanttFigure` (pure/testable).
 */
import { useMemo } from "react";
import { ChartFrame } from "@components/chart";
import { useAppSelector } from "@app/hooks";
import type { PlanTimelineResponse } from "@api/types";
import { buildGanttFigure, type GanttPalette } from "./buildGanttFigure";
import { isCpeTrack, type PlanActualMode, type ZoomScale } from "./plannerModel";

/**
 * Resolve a token to a concrete rgb() string. The --op-* tokens are authored as
 * `color-mix(...)` (tokens.css §20), which getPropertyValue returns verbatim —
 * Plotly + our alpha helper need a resolved colour, so we let the browser paint
 * it on a throwaway element and read back the computed rgb. Falls back to the
 * raw value (jsdom has no layout engine) so tests still get a usable string.
 */
function resolveColor(raw: string, fallback: string): string {
  const value = raw || fallback;
  if (typeof document === "undefined") return value;
  const probe = document.createElement("span");
  probe.style.color = value;
  probe.style.display = "none";
  document.body.appendChild(probe);
  const computed = getComputedStyle(probe).color;
  document.body.removeChild(probe);
  return computed || value;
}

/** Read a custom property and resolve it to a concrete colour. */
function readColor(name: string, fallback: string): string {
  if (typeof document === "undefined") return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return resolveColor(raw, fallback);
}

/** Read a non-colour custom property (e.g. a font stack) verbatim. */
function readVar(name: string, fallback: string): string {
  if (typeof document === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

function usePalette(): GanttPalette {
  // Re-read when theme/tenant/palette change (they alter the resolved tokens).
  const theme = useAppSelector((s) => s.prefs.theme);
  const tenant = useAppSelector((s) => s.prefs.tenant);
  return useMemo<GanttPalette>(
    () => ({
      drilling: readColor("--op-drilling", "#4a9fe0"),
      completion: readColor("--op-completion", "#3ecf8e"),
      pilot: readColor("--op-pilot", "#6aa9e0"),
      workover: readColor("--op-workover", "#9b8cff"),
      pulling: readColor("--op-pulling", "#d98c5f"),
      wireline: readColor("--op-wireline", "#c0a34f"),
      ct: readColor("--op-ct", "#4fd0c0"),
      pad: readColor("--op-pad", "#8a94a6"),
      unconv: readColor("--op-unconv", "#5fbf9f"),
      npt: readColor("--crit", "#f0576a"),
      spine: readColor("--oxbo-z", "#4a9fe0"),
      ink: readColor("--ink", "#eef0f3"),
      dim: readColor("--dim", "#aeb4c0"),
      line: readColor("--line", "#2a3140"),
      fontMono: readVar("--font-mono", "ui-monospace, monospace"),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme, tenant],
  );
}

const ROW_H = 34;

export interface PlannerTimelineProps {
  resp: PlanTimelineResponse;
  mode: PlanActualMode;
  scale: ZoomScale;
  autoZoom: boolean;
  title: string;
  subtitle?: string;
}

export function PlannerTimeline({
  resp,
  mode,
  scale,
  autoZoom,
  title,
  subtitle,
}: PlannerTimelineProps) {
  const palette = usePalette();
  const now = Date.now();

  const fig = useMemo(
    () => buildGanttFigure(resp, { palette, now, mode, scale, autoZoom }),
    [resp, palette, now, mode, scale, autoZoom],
  );

  return (
    <div className="planner-gantt">
      <div className="planner-rail" aria-hidden="true">
        {/* Spacer aligns the rail with the chart's top axis + margin. */}
        <div className="planner-rail-head" />
        {(resp.tracks ?? []).map((track) => (
          <div
            key={track.id}
            className={`planner-rail-row${isCpeTrack(track) ? " cpe" : ""}`}
            style={{ height: ROW_H }}
          >
            <span className="planner-rail-label">{track.label}</span>
            <span className="planner-rail-kind">{track.kind}</span>
          </div>
        ))}
      </div>
      <div className="planner-canvas">
        <ChartFrame
          title={title}
          subtitle={subtitle}
          data={fig.data}
          layout={fig.layout}
          height={fig.height}
          ariaLabel={`${title} plan-vs-actual timeline`}
        />
      </div>
    </div>
  );
}
